// idle-state-tracker.service.ts
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, fromEvent, merge, timer } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { LoggerService } from './logger.service';

/**
 * Service that tracks user idle state based on DOM events
 */
@Injectable({
    providedIn: 'root',
})
export class IdleStateTrackerService implements OnDestroy {
    private readonly idleTimeout = 5000; // 5 seconds
    private readonly isIdle$ = new BehaviorSubject<boolean>(false);
    private subscription: Subscription | null = null;

    constructor(private ngZone: NgZone, protected logger: LoggerService) {}

    /**
     * Start tracking idle state and return an observable
     */
    startTracking(): Observable<boolean> {
        if (this.subscription) {
            this.logger.log('Tracking is already active');
            return this.isIdle$.asObservable();
        }

        this.ngZone.runOutsideAngular(() => {
            const events = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart', 'touchmove', 'scroll', 'click'];

            const activityEvents$ = merge(
                ...events.map((event) => fromEvent(document, event)),
                fromEvent(window, 'focus')
            );

            this.subscription = activityEvents$.pipe(
                tap(() => this.ngZone.run(() => this.isIdle$.next(false))),
                switchMap(() => timer(this.idleTimeout).pipe(
                    tap(() => {
                        this.ngZone.run(() => this.isIdle$.next(true));
                        this.logger.log('User is idle');
                    })
                ))
            ).subscribe();
        });

        return this.isIdle$.asObservable();
    }

    /**
     * Check if user is currently idle
     */
    isIdle(): boolean {
        return this.isIdle$.getValue();
    }

    /**
     * Destroy tracking and cleanup
     */
    destroyTracking(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
            this.subscription = null;
        }
        this.isIdle$.complete();
        this.isIdle$.next(false);
        this.logger.log('Idle tracking destroyed and cleaned up');
    }

    ngOnDestroy(): void {
        this.destroyTracking();
    }
}
